/** Known issues and recent fixes: what the reply drafter knows is going on. */
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError, api } from '../api/client'
import { useRepliesEnabled } from '../lib/useRepliesEnabled'

type Issue = { id: number; title: string; body: string; publication: string | null; status: 'open' | 'fixed';
  started_on: string | null; fixed_on: string | null; created_by: string; updated_at: string }

const TITLES = [['', 'All titles'], ['flight_journal', 'Flight Journal'], ['model_airplane_news', 'Model Airplane News'],
  ['rc_car_action', 'RC Car Action'], ['diecast', 'Die Cast X']] as const

function issueErrText(err: unknown): string {
  if (err instanceof ApiError) {
    const d = err.detail as { detail?: unknown } | null
    if (err.status === 422) return 'Check the title and description.'
    if (d && typeof d.detail === 'string') return d.detail
    return `Not saved (error ${err.status}).`
  }
  return 'Not saved. Check your connection and try again.'
}

export function KnownIssuesPage() {
  const qc = useQueryClient()
  const enabled = useRepliesEnabled()
  const list = useQuery({
    queryKey: ['known-issues'],
    queryFn: () => api.get<Issue[]>('/api/replies/known-issues'),
    enabled: enabled.data === true,
  })
  const [title, setTitle] = useState(''); const [body, setBody] = useState(''); const [pub, setPub] = useState('')
  const add = useMutation({
    mutationFn: () => api.post('/api/replies/known-issues', { title, body, publication: pub || null }),
    onSuccess: () => { setTitle(''); setBody(''); qc.invalidateQueries({ queryKey: ['known-issues'] }) },
  })
  const fix = useMutation({
    mutationFn: (id: number) => api.patch(`/api/replies/known-issues/${id}`,
      { status: 'fixed', fixed_on: new Date().toISOString().slice(0, 10) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['known-issues'] }),
  })
  if (enabled.data === false) {
    return <p className="p-8 text-paper-trace">Drafted replies are switched off on this server.</p>
  }
  return (
    <div className="p-8 space-y-6 max-w-[1000px]">
      <header className="border-b border-ink-500 pb-6">
        <div className="label mb-1">Members · replies</div>
        <h1 className="font-serif text-[42px] leading-[1.05] tracking-tight text-paper">Known issues</h1>
        <p className="mt-2 text-sm text-paper-dim max-w-[62ch]">
          Current problems and recent fixes. The reply drafter reads open issues and anything fixed in the
          last 30 days, so write each one the way you'd explain it to a member.
        </p>
        <Link to="/help/reply-desk" className="label hover:underline self-end whitespace-nowrap">Reply desk guide</Link>
      </header>
      <form className="space-y-2" onSubmit={(e) => { e.preventDefault(); add.mutate() }}>
        <input id="ki-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Short title"
          className="w-full bg-ink-900 border border-ink-600 text-paper p-2" />
        <textarea id="ki-body" rows={3} value={body} onChange={(e) => setBody(e.target.value)}
          placeholder="What members see, and what we did about it"
          className="w-full bg-ink-900 border border-ink-600 text-paper p-2" />
        <select id="ki-pub" value={pub} onChange={(e) => setPub(e.target.value)}
          className="bg-ink-900 border border-ink-500 px-3 py-2 text-paper font-mono text-[12px]">
          {TITLES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <button type="submit" className="label-amber hover:underline ml-3"
          disabled={!title.trim() || !body.trim() || add.isPending}>Add issue</button>
        {add.isError && <p className="text-red-400 text-sm">{issueErrText(add.error)}</p>}
        {fix.isError && <p className="text-red-400 text-sm">{issueErrText(fix.error)}</p>}
      </form>
      {list.isError && <p className="text-red-400">Couldn't load known issues. Try again.</p>}
      <ul className="space-y-3">
        {(list.data ?? []).map((k) => (
          <li key={k.id} className="border border-ink-500 p-4 space-y-1">
            <div className="flex flex-wrap gap-3 items-baseline">
              <span className={k.status === 'open' ? 'label-amber' : 'label'}>{k.status === 'open' ? 'OPEN' : `FIXED ${k.fixed_on ?? ''}`}</span>
              <span className="text-paper font-semibold">{k.title}</span>
              <span className="label">{k.publication ?? 'all titles'}</span>
              {k.status === 'open' && (
                <button type="button" className="label hover:underline ml-auto" onClick={() => fix.mutate(k.id)}>Mark fixed today</button>
              )}
            </div>
            <p className="text-paper-dim text-sm whitespace-pre-wrap">{k.body}</p>
          </li>
        ))}
      </ul>
    </div>
  )
}
